import axios, { GenericAbortSignal } from 'axios'

import { getApiClient } from '@/api'
import { ROUTES } from '@/constants'

import { TQrContent } from '../../generatorTypes'
import { TQrDesign } from '../client'

export type MalwareStatsJson = {
	containsMalware?: boolean
}

export type TQrData<T extends TQrContent = TQrContent> = {
	id: string
	content: T
	qrDesign: TQrDesign | null
	uri: string
	userLanguage: string
	createdAt: string
	updatedAt: string
	name: string
	scansAmount: number
	disabled: boolean
	autoDisabled: boolean
	malwareStatsJson?: MalwareStatsJson
	isLocked?: boolean
	hasPassword?: boolean
}

export type TRequiredQrData<T extends TQrContent> = Pick<
	TQrData<T>,
	'name' | 'content' | 'qrDesign'
>

export type TPostQrResponse = Pick<TQrData<TQrContent>, 'id' | 'uri'>

/**
 * Adds fields that are required for BE validation but are not used in FE app
 */
const addRequiredButUnusedQrDesignFields = (qrDesign: TQrDesign | null) => {
	if (qrDesign === null) {
		return null
	}
	const requiredButUnusedFields = {
		data: '',
		type: 'svg',
		image: {
			selected: null,
			uploaded: null,
		},
		width: 210,
		height: 210,
		margin: 0,
		qrOptions: {
			mode: 'Byte',
			typeNumber: 0,
			errorCorrectionLevel: 'Q',
			byteModeStringEncoding: 'UTF-8',
		},
		imageOptions: {
			margin: 0,
			imageSize: 0.5,
			hideBackgroundDots: true,
		},
	} as TQrDesign
	return { ...requiredButUnusedFields, ...qrDesign }
}

const getQrList = async () => getApiClient().get<TQrData[]>(`/qr-codes`)

const getQrIdsList = async () => getApiClient().get<string[]>(`/qr-codes/ids`)

const getQrScanList = async () =>
	getApiClient().get<Pick<TQrData, 'id' | 'createdAt' | 'scansAmount'>[]>(
		`/qr-codes/scan-count`,
	)

const getQr = <T extends TQrContent>(qrId: string) =>
	getApiClient().get<TQrData<T>>(`/qr-codes/${qrId}`)

const getGuestQr = <T extends TQrContent>(qrId: string) =>
	getApiClient().get<TQrData<T>>(`/qr-codes/guest/${qrId}`)

const postQr = <T extends TQrContent>(
	data: TRequiredQrData<T>,
	abortSignal?: GenericAbortSignal,
) =>
	getApiClient().post<TPostQrResponse>(
		'/qr-codes',
		{
			...data,
			qrDesign: addRequiredButUnusedQrDesignFields(data.qrDesign),
		},
		{ signal: abortSignal },
	)

const postQrPreview = (id: string) =>
	getApiClient().post<TPostQrResponse>(`/qr-codes/${id}/preview`)

const postGuestQr = <T extends TQrContent>(
	data: TRequiredQrData<T>,
	abortSignal?: GenericAbortSignal,
) =>
	getApiClient().post<TPostQrResponse>(
		'/qr-codes/guest',
		{
			...data,
			qrDesign: addRequiredButUnusedQrDesignFields(data.qrDesign),
		},
		{ signal: abortSignal },
	)

const putQr = <T extends TQrContent>(id: string, data: TRequiredQrData<T>) =>
	getApiClient().put<void>(`/qr-codes/${id}`, {
		...data,
		qrDesign: addRequiredButUnusedQrDesignFields(data.qrDesign),
	})

const putQuestQr = <T extends TQrContent>(
	id: string,
	data: TRequiredQrData<T>,
) =>
	getApiClient().put<void>(`/qr-codes/guest/${id}`, {
		...data,
		qrDesign: addRequiredButUnusedQrDesignFields(data.qrDesign),
	})

const getQrByUri = <T extends TQrContent>(uri: string, unlockToken?: string) =>
	getApiClient().get<TQrData<T>>(`/common/qr-codes/uri/${uri}`, {
		headers: unlockToken ? { 'X-QR-Unlock-Token': unlockToken } : undefined,
	})

const publishQr = (id: string) =>
	getApiClient().put<void>(`/qr-codes/publish/${id}`)

const publishQrPreview = <T extends TQrContent>({
	targetId,
	previewQRCodeId,
	qrCodeDto,
	password,
}: {
	targetId: string
	previewQRCodeId: string
	qrCodeDto: TRequiredQrData<T>
	password?: string | null
}) =>
	getApiClient().put<TPostQrResponse>(`/qr-codes/publish-preview/${targetId}`, {
		previewQRCodeId,
		qrCodeDto: {
			...qrCodeDto,
			qrDesign: addRequiredButUnusedQrDesignFields(qrCodeDto.qrDesign),
		},
		password,
	})

const deactivateQrs = (ids: string[]) =>
	getApiClient().post('/qr-codes/deactivate', { ids })

const activateQrs = (ids: string[]) =>
	getApiClient().post('/qr-codes/activate', { ids })

const resetScansQrs = (ids: string[]) =>
	getApiClient().post('/qr-codes/reset-scans', { ids })

const deleteQrs = (ids: string[]) =>
	getApiClient().delete('/qr-codes', { data: { ids } })

export const buildDisabledRedirectUrl = (error: unknown): string => {
	if (!axios.isAxiosError(error) || error.response?.status !== 451) {
		return ROUTES.qrDisabled
	}
	const lang = error.response.headers['x-qr-language']
	return typeof lang === 'string' && lang.length > 0
		? `${ROUTES.qrDisabledLangProxy}?lang=${encodeURIComponent(lang)}`
		: ROUTES.qrDisabled
}

const getQrPassword = (qrId: string) =>
	getApiClient().get<{ password: string }>(`/qr-codes/${qrId}/password`)

const putQrPassword = (qrId: string, password: string) =>
	getApiClient().put<{ hasPassword: boolean }>(`/qr-codes/${qrId}/password`, {
		password,
	})

const deleteQrPassword = (qrId: string) =>
	getApiClient().delete<void>(`/qr-codes/${qrId}/password`)

const putGuestQrPassword = (qrId: string, password: string) =>
	getApiClient().put<{ hasPassword: boolean }>(
		`/qr-codes/guest/${qrId}/password`,
		{ password },
	)

const deleteGuestQrPassword = (qrId: string) =>
	getApiClient().delete<void>(`/qr-codes/guest/${qrId}/password`)

export const qrCodesApi = {
	getQrList,
	getQrIdsList,
	getQrScanList,
	getQr,
	getGuestQr,
	postQr,
	postQrPreview,
	postGuestQr,
	putQr,
	putQuestQr,
	getQrByUri,
	publishQr,
	publishQrPreview,
	deactivateQrs,
	activateQrs,
	resetScansQrs,
	deleteQrs,
	getQrPassword,
	putQrPassword,
	deleteQrPassword,
	putGuestQrPassword,
	deleteGuestQrPassword,
}
